import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Button, Input, Table, Drawer } from "antd";
import Close from "../../../../../assets/images/close-x.svg";

const StockConfirmationDrawer = ({
  isStockModal,
  setIsStockModal,
  cart,
  tempCart,
  setTempCart,
  addProduct,
  deleteProduct,
  deleteCart,
  handleCartTotalClick,
  themeJSON,
}) => {
  const [itemWeights, setItemWeights] = useState(() => {
    const initialWeights = {};
    cart?.items.forEach((item) => {
      initialWeights[item.lineId] = item.availableQty;
    });
    return initialWeights;
  });
  const inputRefs = useRef([]);

  useEffect(() => {
    const initialWeights = {};
    cart?.items.forEach((item) => {
      initialWeights[item.lineId] = item.weight <= item?.availableQty ? item.weight : item?.availableQty || 0;
    });
    setItemWeights(initialWeights);
  }, [cart]);

  useEffect(() => {
    if (isStockModal) {
      const timeout = setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }, 0);
      return () => clearTimeout(timeout);
    }
  }, [isStockModal]);

  const handleQtyChange = (value, recordIndex, productId, isDecimal) => {
    let sanitizedValue = value.replace(/[^0-9.]/g, "");
    if (!isDecimal) {
      sanitizedValue = sanitizedValue.replaceAll(".", "");
    }

    if (isDecimal) {
      const parts = sanitizedValue.split(".");
      if (parts.length > 2) {
        sanitizedValue = parts[0] + "." + parts.slice(1).join("");
      }
      if (parts.length === 2 && parts[1].length > 3) {
        sanitizedValue = parts[0] + "." + parts[1].substring(0, 3);
      }
    }

    let displayValue = sanitizedValue;
    if (sanitizedValue.startsWith("0") && sanitizedValue.length > 1 && !sanitizedValue.includes(".")) {
      displayValue = sanitizedValue.substring(1);
    }

    displayValue = displayValue === "" ? "0" : displayValue;

    const updatedTempCart = { ...tempCart };
    let tempData = { ...itemWeights };
    tempData[productId] = displayValue;
    setItemWeights(tempData);

    const parsedWeight = parseFloat(displayValue);
    if (!isNaN(parsedWeight)) {
      updatedTempCart[productId] = parsedWeight;
      setTempCart(updatedTempCart);
    }
  };

  const handleKeyDown = (e, isDecimal, index) => {
    let totalItems = (cart?.items || []).filter((item) => item.weight !== 0);
    if (!isDecimal && e.key === ".") {
      e.preventDefault();
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (index < totalItems.length - 1) {
        inputRefs.current[index + 1]?.focus();
      } else {
        handleSubmit();
        setIsStockModal(false);
      }
    }
  };

  const handleSubmit = async () => {
    let showPaymentsFlag = false;
    let deleteItems = [];
    await Promise.all(
      tempCart.items.map((item) => {
        if (itemWeights[item.lineId] > 0) {
          showPaymentsFlag = true;
          if (item.weight !== itemWeights[item.lineId]) {
            item.modified = true;
            return addProduct(item, itemWeights[item.lineId], true);
          }
        } else {
          deleteItems.push(item);
        }
      })
    );
    if (tempCart.items.length !== deleteItems.length) {
      await Promise.all(deleteItems.map((item) => { deleteProduct(item); }));
    } else {
      deleteCart();
    }
    if (showPaymentsFlag) {
      handleCartTotalClick();
    }
  };

  const columns = [
    {
      title: "Item Name",
      dataIndex: "name",
      key: "name",
      width: "32%",
      render: (text, record) => (
        <>
          <div id="sm-product-name" className="tableRow">{record.name}</div>
          <div className="offerName">
            {record.availableQty >= record.weight ? (
              <p style={{ margin: 0, color: "#009000" }}>Available</p>
            ) : record.availableQty > 0 && record.weight > 0 ? (
              <p style={{ margin: 0, color: "#FF5E0E" }}>Only {record.availableQty} Available</p>
            ) : (
              <p style={{ margin: 0, color: "red" }}>Out Of Stock</p>
            )}
          </div>
        </>
      ),
    },
    {
      title: "UOM",
      dataIndex: "uom_name",
      key: "uom_name",
      width: "12%",
      render: (text) => <div style={{ textAlign: "left" }}>{text}</div>,
      align: "center",
    },
    {
      title: "Order Qty",
      dataIndex: "weight",
      key: "weight",
      width: "15%",
      align: "right",
      render: (text) => <div style={{ textAlign: "right" }}>{parseFloat(text).toFixed(2)}</div>,
    },
    {
      title: "Available Qty",
      dataIndex: "availableQty",
      key: "availableQty",
      width: "15%",
      align: "right",
      render: (text) => (
        <div style={{ textAlign: "right", color: text > 0 ? "#0F0718" : "red" }}>
          {text?.toFixed(2).length < 8 ? parseFloat(text)?.toFixed(2) : `${text?.toString().substring(0, 8)}`}
        </div>
      ),
    },
    {
      title: "Update Order Qty",
      dataIndex: "availableQty",
      key: "updateQty",
      width: "22%",
      align: "center",
      render: (text, record, index) => (
        <Input
          key={record.lineId}
          style={{ width: "10vw", textAlign: "right" }}
          ref={(el) => (inputRefs.current[index] = el)}
          value={itemWeights[record.lineId]}
          onChange={(e) => handleQtyChange(e.target.value, index, record.lineId, record.isDecimal)}
          onPressEnter={(e) => e.preventDefault()}
          onKeyDown={(e) => handleKeyDown(e, record.isDecimal, index)}
        />
      ),
    },
  ];

  return (
    <Drawer
      closable={true}
      placement="bottom"
      title={
        <div style={{ display: "flex", alignItems: "center", height: "2vh", fontSize: "1vw" }}>
          <p style={{ fontSize: "1.5em", marginTop: "3vh", fontWeight: "500", color: "#0F0718", flex: 1, textAlign: "center" }}>
            Stock Confirmation
          </p>
          <img
            src={Close}
            onClick={() => setIsStockModal(false)}
            style={{ float: "right", cursor: "pointer", marginTop: "-3vh", marginRight: "-0.5vw", width: "1.5vw" }}
          />
        </div>
      }
      closeIcon={null}
      open={isStockModal}
      className="filter"
      styles={{ body: { padding: "0 4vw" } }}
      onClose={() => setIsStockModal(false)}
      height="90vh"
    >
      <Table
        className="sm-cart-table-pos-stock"
        style={{
          width: "100%",
          overflowY: "auto",
          border: "none",
          borderRadius: "7px",
          padding: "0px 7px",
          height: "67vh",
          background: themeJSON?.productTableBGColor || "#fff",
        }}
        pagination={false}
        dataSource={(cart?.items || []).filter((item) => item.availableQty < item.weight)}
        sticky
        id="sm-cart-table"
        columns={columns}
      />
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          style={{ backgroundColor: "#fff", color: "#2F3856", height: "7vh", width: "15vw", borderRadius: "7px", marginTop: "1rem" }}
          onClick={() => setIsStockModal(false)}
        >
          Cancel
        </Button>
        <Button
          style={{ backgroundColor: "#2F3856", color: "#fff", height: "7vh", width: "15vw", borderRadius: "7px", marginTop: "1rem", marginLeft: "1rem" }}
          onClick={() => {
            handleSubmit();
            setIsStockModal(false);
          }}
        >
          Confirm
        </Button>
      </div>
    </Drawer>
  );
};

export default StockConfirmationDrawer;
